import React, { useState, useEffect, useRef } from 'react';
import { VscAdd, VscTrash, VscCheck, VscClose, VscGraph } from 'react-icons/vsc';
import { IoCalculatorOutline } from 'react-icons/io5';
import { getQuotes, createQuote, deleteQuote } from '../../api/quotes';

// Other quote views announce saves/deletes with this event so the list stays current
export const QUOTES_CHANGED = 'quotes-changed';
export const announceQuotesChanged = (detail) => window.dispatchEvent(new CustomEvent(QUOTES_CHANGED, { detail }));

function QuoteSidebar({ project, activeTabId, onOpenQuote, onOpenSummary }) {
  const [quotes, setQuotes]     = useState([]);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName]   = useState('');
  const inputRef = useRef(null);

  const load = () => getQuotes(project._id).then(r => setQuotes(r.data)).catch(() => {});

  useEffect(() => {
    if (!project?._id) return;
    load();
    const onChange = (e) => { if (!e.detail?.project || e.detail.project === project._id) load(); };
    window.addEventListener(QUOTES_CHANGED, onChange);
    return () => window.removeEventListener(QUOTES_CHANGED, onChange);
  }, [project?._id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (creating) inputRef.current?.focus(); }, [creating]);

  const handleCreate = async () => {
    try {
      const res = await createQuote({ project: project._id, name: newName.trim() });
      setQuotes(prev => [...prev, res.data]);
      setCreating(false); setNewName('');
      onOpenQuote(res.data);
      announceQuotesChanged({ project: project._id });
    } catch {}
  };

  const handleDelete = async (q) => {
    if (!confirm(`Delete quote ${q.number} "${q.name}"? This can't be undone.`)) return;
    try {
      await deleteQuote(q._id);
      setQuotes(prev => prev.filter(x => x._id !== q._id));
      announceQuotesChanged({ project: project._id, deleted: q._id });
    } catch {}
  };

  return (
    <nav className="sp-nav">
      <button className={`sp-nav-item${activeTabId === `quote-summary-${project._id}` ? ' active' : ''}`} onClick={onOpenSummary}>
        <VscGraph size={14} style={{ color: '#c586c0', flexShrink: 0 }} />
        Summary
      </button>

      <div className="sp-subheader">Quotes</div>
      {quotes.map(q => (
        <div key={q._id} className="sp-project-row">
          <button
            className={`sp-nav-item sp-nav-indent${activeTabId === `quote-${q._id}` ? ' active' : ''}`}
            style={{ flex: 1 }}
            onClick={() => onOpenQuote(q)}
            title={`${q.number} — ${q.name}`}
          >
            <IoCalculatorOutline size={14} style={{ color: '#c586c0', flexShrink: 0 }} />
            <span className="sp-quote-name">{q.name}</span>
            <span className="sp-quote-no">{q.number}</span>
          </button>
          <button className="btn-icon sp-diagram-btn" title="Delete" onClick={() => handleDelete(q)}><VscTrash size={12} /></button>
        </div>
      ))}

      {creating ? (
        <div className="sp-diagram-new-row">
          <input
            ref={inputRef}
            className="sp-diagram-rename"
            value={newName}
            placeholder="Quote name…"
            onChange={e => setNewName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleCreate(); if (e.key === 'Escape') { setCreating(false); setNewName(''); } }}
          />
          <button className="btn-icon" onClick={handleCreate}><VscCheck size={13} /></button>
          <button className="btn-icon" onClick={() => { setCreating(false); setNewName(''); }}><VscClose size={13} /></button>
        </div>
      ) : (
        <button className="sp-nav-item sp-nav-indent" style={{ color: 'var(--text-muted)', gap: 6 }} onClick={() => setCreating(true)}>
          <VscAdd size={13} /> New Quote
        </button>
      )}
    </nav>
  );
}

export default QuoteSidebar;
